import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import { Beaker, FlaskConical, Upload, Sparkles, Brain } from "lucide-react";

export default function LandingPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-noir">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <FlaskConical className="w-6 h-6 text-amber" />
            <span className="font-playfair text-xl font-semibold text-white">Scentic</span>
          </Link>
          <div className="flex items-center gap-4">
            {user ? (
              <Link to="/dashboard">
                <Button 
                  data-testid="dashboard-btn"
                  className="bg-amber hover:bg-amber-light text-black font-semibold uppercase tracking-widest text-xs px-6"
                >
                  Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" data-testid="login-btn" className="text-zinc-300 hover:text-white">
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    data-testid="register-btn"
                    className="bg-amber hover:bg-amber-light text-black font-semibold uppercase tracking-widest text-xs px-6"
                  >
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1760860992203-85ca32536788?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxwZXJmdW1lJTIwYm90dGxlJTIwZGFyayUyMGx1eHVyeSUyMGxpZ2h0aW5nfGVufDB8fHx8MTc2ODI2NzE1NXww&ixlib=rb-4.1.0&q=85)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-noir via-noir/80 to-noir" />
        
        <div className="relative max-w-7xl mx-auto px-6 pt-24">
          <div className="max-w-3xl">
            <h1 className="font-playfair text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight tracking-tight mb-6 animate-fade-in">
              Craft Your
              <span className="text-amber"> Signature</span>
              <br />Fragrance
            </h1>
            <p className="text-zinc-400 text-lg md:text-xl mb-10 max-w-xl">
              The professional perfumery formulation platform. Manage ingredients, 
              calculate recipes, and let AI assist your creative process.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to={user ? "/dashboard" : "/register"}>
                <Button 
                  data-testid="hero-cta-btn"
                  size="lg"
                  className="bg-amber hover:bg-amber-light text-black font-semibold uppercase tracking-widest text-sm px-8 py-6 amber-glow"
                >
                  Start Creating
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-noir-paper">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-white mb-4">
              Everything You Need
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Professional tools designed for perfumers, by perfumers
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard 
              icon={<Beaker className="w-6 h-6" />}
              title="Material Inventory"
              description="Track your ingredients with CAS numbers, IFRA limits, and odor profiles"
            />
            <FeatureCard 
              icon={<FlaskConical className="w-6 h-6" />}
              title="Recipe Builder"
              description="Calculate precise formulas with automatic percentage and gram conversions"
            />
            <FeatureCard 
              icon={<Upload className="w-6 h-6" />}
              title="Smart Import"
              description="Upload PDF, Excel, or text files and let AI parse your recipes"
            />
            <FeatureCard 
              icon={<Brain className="w-6 h-6" />}
              title="AI Assistant"
              description="Auto-generate descriptions, lookup CAS numbers and IFRA limits"
            />
          </div>
        </div>
      </section>

      {/* AI Features Section */}
      <section className="py-24 bg-noir">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber/10 text-amber px-4 py-2 rounded-full mb-6">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium">AI-Powered</span>
              </div>
              <h2 className="font-playfair text-3xl md:text-4xl font-bold text-white mb-6">
                Intelligent Assistance
              </h2>
              <div className="space-y-4">
                <AIFeature 
                  title="Auto-Generate Descriptions"
                  description="Get professional descriptions for your materials instantly"
                />
                <AIFeature 
                  title="IFRA Limit Lookup"
                  description="Automatically fetch regulatory limits for any ingredient"
                />
                <AIFeature 
                  title="CAS Number Search"
                  description="Find CAS registry numbers for your materials"
                />
                <AIFeature 
                  title="Recipe Parsing"
                  description="Upload any format and convert it to a usable formula"
                />
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1758876569703-ea9b21463691?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxjaGVtaXN0cnklMjBsYWIlMjBnbGFzc3dhcmUlMjBhZXN0aGV0aWN8ZW58MHx8fHwxNzY4MjY3MTU5fDA&ixlib=rb-4.1.0&q=85"
                alt="Chemistry Lab"
                className="rounded-none border border-zinc-800"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-noir via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-noir-paper">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Create?
          </h2>
          <p className="text-zinc-400 mb-8">
            Join perfumers worldwide who trust Scentic for their formulations
          </p>
          <Link to={user ? "/dashboard" : "/register"}>
            <Button 
              data-testid="cta-get-started-btn"
              size="lg"
              className="bg-amber hover:bg-amber-light text-black font-semibold uppercase tracking-widest text-sm px-10 py-6"
            >
              Get Started Free
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-amber" />
            <span className="font-playfair text-white">Scentic</span>
          </div>
          <p className="text-zinc-500 text-sm">
            © 2025 Scentic. Professional Perfumery Formulation.
          </p>
        </div>
      </footer>
    </div>
  );
}

const FeatureCard = ({ icon, title, description }) => (
  <div className="bg-noir-subtle border border-zinc-800 p-6 hover:border-amber/30 transition-colors group">
    <div className="w-12 h-12 bg-amber/10 flex items-center justify-center mb-4 text-amber group-hover:bg-amber/20 transition-colors">
      {icon}
    </div>
    <h3 className="font-semibold text-white mb-2">{title}</h3>
    <p className="text-zinc-400 text-sm">{description}</p>
  </div>
);

const AIFeature = ({ title, description }) => (
  <div className="flex gap-4">
    <div className="w-2 h-2 rounded-full bg-amber mt-2 flex-shrink-0" />
    <div>
      <h4 className="font-medium text-white mb-1">{title}</h4>
      <p className="text-zinc-400 text-sm">{description}</p>
    </div>
  </div>
);
